import { z } from "zod";
import { OkResponse } from "./common";

/**
 * org_members.role / org_invites.role — TEXT columns constrained by
 * a CHECK to these three values (supabase/schemas/orgs.sql).
 */
export const OrgRole = z.enum(["owner", "member", "contributor"]);
export type OrgRole = z.infer<typeof OrgRole>;

/**
 * Full `orgs` table row. The org handlers return whole rows
 * (`select("*")`, RPC return, `update().select().single()`), so the
 * contract carries every column — respondWith strips anything the
 * schema omits.
 */
export const Org = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  is_personal: z.boolean(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Org = z.infer<typeof Org>;

// ── POST /api/orgs ────────────────────────────────────────────────

export const OrgCreateRequest = z.object({
  name: z.string().min(2).max(80),
});
export type OrgCreateRequest = z.infer<typeof OrgCreateRequest>;

export const OrgCreateResponse = z.object({ org: Org });
export type OrgCreateResponse = z.infer<typeof OrgCreateResponse>;

// ── GET /api/me/orgs ──────────────────────────────────────────────

/**
 * The handler selects `*, org_members!inner(role)`, so each row is
 * the full org plus the caller's membership join artifact. No
 * consumer reads `org_members` today, but it's on the wire and the
 * contract must capture the full payload. `role` is deliberately a
 * plain string (not OrgRole): the artifact is unused, so looser
 * parsing here cannot 500 a read if roles ever change.
 */
export const MyOrg = Org.extend({
  org_members: z.array(z.object({ role: z.string() })),
});
export type MyOrg = z.infer<typeof MyOrg>;

export const MyOrgsResponse = z.array(MyOrg);
export type MyOrgsResponse = z.infer<typeof MyOrgsResponse>;

// ── PATCH /api/o/:orgSlug ─────────────────────────────────────────

export const OrgUpdateRequest = z.object({
  name: z.string().min(2).max(80),
});
export type OrgUpdateRequest = z.infer<typeof OrgUpdateRequest>;

/** PATCH returns the updated org row unwrapped. */
export const OrgUpdateResponse = Org;
export type OrgUpdateResponse = z.infer<typeof OrgUpdateResponse>;

// ── /api/o/:orgSlug/members ───────────────────────────────────────

export const OrgMember = z.object({
  // Response-side ids stay bare z.string() (convention across all
  // contracts): a malformed id should not turn a read into a 500.
  // Request-side ids below keep .uuid() as input validation.
  user_id: z.string(),
  role: OrgRole,
  created_at: z.string(),
  /** Resolved via the auth admin API; null when the user is gone. */
  email: z.string().nullable(),
});
export type OrgMember = z.infer<typeof OrgMember>;

export const OrgMembersResponse = z.array(OrgMember);
export type OrgMembersResponse = z.infer<typeof OrgMembersResponse>;

export const OrgMemberUpdateRequest = z.object({
  user_id: z.string().uuid(),
  role: OrgRole,
});
export type OrgMemberUpdateRequest = z.infer<typeof OrgMemberUpdateRequest>;

export const OrgMemberDeleteRequest = z.object({
  user_id: z.string().uuid(),
});
export type OrgMemberDeleteRequest = z.infer<typeof OrgMemberDeleteRequest>;

export const OrgMemberMutationResponse = OkResponse;
export type OrgMemberMutationResponse = z.infer<
  typeof OrgMemberMutationResponse
>;

// ── /api/o/:orgSlug/invites ───────────────────────────────────────

/**
 * List item for GET /api/o/:orgSlug/invites — the handler selects
 * exactly these columns (no token: list responses don't leak invite
 * tokens). `invited_by` is NOT NULL in the schema.
 */
export const OrgInvite = z.object({
  id: z.string(),
  email: z.string(),
  role: OrgRole,
  expires_at: z.string(),
  created_at: z.string(),
  invited_by: z.string(),
});
export type OrgInvite = z.infer<typeof OrgInvite>;

export const OrgInvitesResponse = z.array(OrgInvite);
export type OrgInvitesResponse = z.infer<typeof OrgInvitesResponse>;

/**
 * Full org_invites row, returned by POST (insert().select().single()).
 * Includes `token` deliberately — when the email transport is down,
 * the caller copies the accept link from this response.
 */
export const OrgInviteRow = OrgInvite.extend({
  org_id: z.string(),
  token: z.string(),
  accepted_at: z.string().nullable(),
});
export type OrgInviteRow = z.infer<typeof OrgInviteRow>;

export const OrgInviteCreateRequest = z.object({
  email: z.string().email().toLowerCase(),
  role: OrgRole,
});
export type OrgInviteCreateRequest = z.infer<typeof OrgInviteCreateRequest>;

export const OrgInviteCreateResponse = z.object({
  invite: OrgInviteRow,
  email: z.object({
    delivered: z.boolean(),
    /** Present only when delivery failed (e.g. "no-transport"). */
    reason: z.string().optional(),
  }),
});
export type OrgInviteCreateResponse = z.infer<typeof OrgInviteCreateResponse>;

// ── DELETE /api/o/:orgSlug/invites/:id ────────────────────────────

export const OrgInviteRevokeResponse = OkResponse;
export type OrgInviteRevokeResponse = z.infer<typeof OrgInviteRevokeResponse>;
